import jsPDF from 'jspdf';
import type { Inspection } from '../types/index';

export async function exportInspectionPDF(inspection: Inspection): Promise<void> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // ── Helpers ──────────────────────────────────────────────

  function checkPageBreak(neededHeight: number) {
    if (y + neededHeight > pageHeight - margin) {
      pdf.addPage();
      y = margin;
    }
  }

  function drawSectionHeader(title: string) {
    checkPageBreak(12);
    pdf.setFillColor(13, 27, 62);
    pdf.rect(margin, y, contentWidth, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title.toUpperCase(), margin + 3, y + 5.5);
    y += 10;
    pdf.setTextColor(0, 0, 0);
  }

  function drawLabelValue(label: string, value: string, x: number, colWidth: number) {
    pdf.setFontSize(7.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(120, 120, 120);
    pdf.text(label, x, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(30, 30, 30);
    pdf.text(value || '—', x, y + 4.5);
  }

  // ── Header ───────────────────────────────────────────────

  pdf.setFillColor(13, 27, 62);
  pdf.rect(0, 0, pageWidth, 22, 'F');
  pdf.setTextColor(125, 200, 50);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Wire3', margin, 13);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Vehicle Inspection Report', margin + 22, 13);

  const statusColors: Record<string, number[]> = {
    submitted: [59, 130, 246],
    reviewed: [34, 197, 94],
    flagged: [239, 68, 68],
    'in-progress': [245, 158, 11],
  };
  const [r, g, b] = statusColors[inspection.status] ?? [150, 150, 150];
  pdf.setFillColor(r, g, b);
  const statusText = inspection.status.toUpperCase();
  const statusWidth = pdf.getTextWidth(statusText) + 6;
  pdf.roundedRect(pageWidth - margin - statusWidth, 7, statusWidth, 8, 2, 2, 'F');
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text(statusText, pageWidth - margin - statusWidth + 3, 12.5);

  y = 28;

  // ── Vehicle & Inspector Info ──────────────────────────────

  drawSectionHeader('Inspection Details');
  const col1 = margin;
  const col2 = margin + contentWidth / 3;
  const col3 = margin + (contentWidth / 3) * 2;

  drawLabelValue('Vehicle', inspection.vehicleUnitNumber, col1, contentWidth / 3);
  drawLabelValue('Inspector', inspection.inspectorName, col2, contentWidth / 3);
  drawLabelValue('Type', inspection.type.replace('-', ' '), col3, contentWidth / 3);
  y += 10;

  drawLabelValue('Status', inspection.status, col1, contentWidth / 3);
  drawLabelValue(
    'Submitted',
    inspection.submittedAt ? new Date(inspection.submittedAt).toLocaleString() : '—',
    col2,
    contentWidth / 3
  );
  drawLabelValue(
    'Mileage',
    inspection.mileage ? `${inspection.mileage.toLocaleString()} mi` : '—',
    col3,
    contentWidth / 3
  );
  y += 12;

  // ── Results Summary ───────────────────────────────────────

  const allChecks = inspection.categories.flatMap(c => c.checks);
  const passed = allChecks.filter(c => c.status === 'pass').length;
  const failed = allChecks.filter(c => c.status === 'fail').length;
  const na = allChecks.filter(c => c.status === 'na').length;

  drawSectionHeader('Results Summary');
  const boxW = contentWidth / 3 - 3;

  pdf.setFillColor(240, 253, 244);
  pdf.rect(margin, y, boxW, 14, 'F');
  pdf.setTextColor(34, 197, 94);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(String(passed), margin + boxW / 2, y + 9, { align: 'center' });
  pdf.setFontSize(7);
  pdf.setTextColor(100, 100, 100);
  pdf.text('PASSED', margin + boxW / 2, y + 13, { align: 'center' });

  const failX = margin + boxW + 3;
  pdf.setFillColor(254, 242, 242);
  pdf.rect(failX, y, boxW, 14, 'F');
  pdf.setTextColor(239, 68, 68);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(String(failed), failX + boxW / 2, y + 9, { align: 'center' });
  pdf.setFontSize(7);
  pdf.setTextColor(100, 100, 100);
  pdf.text('FAILED', failX + boxW / 2, y + 13, { align: 'center' });

  const naX = margin + (boxW + 3) * 2;
  pdf.setFillColor(248, 250, 252);
  pdf.rect(naX, y, boxW, 14, 'F');
  pdf.setTextColor(148, 163, 184);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(String(na), naX + boxW / 2, y + 9, { align: 'center' });
  pdf.setFontSize(7);
  pdf.setTextColor(100, 100, 100);
  pdf.text('N/A', naX + boxW / 2, y + 13, { align: 'center' });

  y += 18;

  // ── Check Results by Category ─────────────────────────────

  drawSectionHeader('Inspection Checks');

  for (const category of inspection.categories) {
    checkPageBreak(10);
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(13, 27, 62);
    pdf.text(category.name, margin, y);
    y += 5;

    for (const check of category.checks) {
      // Estimate height needed for this check row
      const reasoningLines = check.status === 'fail' && check.aiReasoning
        ? pdf.splitTextToSize(`AI: ${check.aiReasoning}`, contentWidth - 8)
        : [];
      const notesLines = check.status === 'fail' && check.notes
        ? pdf.splitTextToSize(`Note: ${check.notes}`, contentWidth - 8)
        : [];
      const rowHeight = 7 + reasoningLines.length * 4 + notesLines.length * 4;

      checkPageBreak(rowHeight);

      const statusColor: Record<string, number[]> = {
        pass: [34, 197, 94],
        fail: [239, 68, 68],
        na: [148, 163, 184],
      };
      const [cr, cg, cb] = statusColor[check.status ?? 'na'] ?? [148, 163, 184];

      // Row background for failed checks
      if (check.status === 'fail') {
        pdf.setFillColor(254, 242, 242);
        pdf.rect(margin, y - 3.5, contentWidth, rowHeight, 'F');
      }

      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(50, 50, 50);
      pdf.text(check.label, margin + 2, y);

      // Status pill
      const statusLabel = (check.status ?? 'n/a').toUpperCase();
      pdf.setFillColor(cr, cg, cb);
      const pillW = pdf.getTextWidth(statusLabel) + 4;
      pdf.roundedRect(pageWidth - margin - pillW, y - 3.5, pillW, 5, 1.5, 1.5, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(6);
      pdf.setFont('helvetica', 'bold');
      pdf.text(statusLabel, pageWidth - margin - pillW + 2, y);

      y += 5.5;

      // AI reasoning for failed checks
      if (check.status === 'fail' && check.aiReasoning) {
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(180, 60, 60);
        pdf.text(reasoningLines, margin + 4, y);
        y += reasoningLines.length * 4;
      }

      // Manual notes for failed checks
      if (check.status === 'fail' && check.notes) {
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(120, 120, 120);
        pdf.text(notesLines, margin + 4, y);
        y += notesLines.length * 4;
      }

      y += 1.5;
    }
    y += 3;
  }

  // ── Photos ────────────────────────────────────────────────

  const checksWithPhotos = inspection.categories
    .flatMap(c => c.checks)
    .filter(c => c.photos && c.photos.length > 0);

  if (checksWithPhotos.length > 0) {
    drawSectionHeader('Inspection Photos');

    for (const check of checksWithPhotos) {
      for (const photo of check.photos) {
        try {
          const imgData = await loadImageAsBase64(photo.uri);
          const imgProps = pdf.getImageProperties(imgData);
          const imgW = contentWidth / 2 - 5;
          const imgH = (imgProps.height / imgProps.width) * imgW;
          const analysisX = margin + imgW + 8;
          const analysisW = contentWidth / 2 - 5;

          const analysisLines = photo.aiAnalysis
            ? pdf.splitTextToSize(photo.aiAnalysis.description, analysisW - 4)
            : [];
          const issueLines = photo.aiAnalysis?.detectedIssues ?? [];
          const analysisHeight = Math.max(
            imgH,
            12 + analysisLines.length * 4.5 + issueLines.length * 4 + 10
          );

          checkPageBreak(analysisHeight + 12);

          pdf.setFontSize(7.5);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(13, 27, 62);
          pdf.text(check.label, margin, y);
          y += 5;

          pdf.addImage(imgData, 'JPEG', margin, y, imgW, imgH);

          if (photo.aiAnalysis) {
            const ai = photo.aiAnalysis;
            pdf.setFillColor(248, 250, 252);
            pdf.rect(analysisX, y, analysisW, analysisHeight, 'F');

            let ay = y + 5;

            const severityColors: Record<string, number[]> = {
              severe: [239, 68, 68],
              moderate: [245, 158, 11],
              minor: [234, 179, 8],
              none: [34, 197, 94],
            };
            const [sr, sg, sb] = severityColors[ai.severity] ?? [148, 163, 184];
            const severityLabel = ai.hasDamage ? ai.severity.toUpperCase() : 'NO DAMAGE';
            pdf.setFillColor(sr, sg, sb);
            const badgeW = pdf.getTextWidth(severityLabel) + 4;
            pdf.roundedRect(analysisX + 2, ay - 3, badgeW, 5, 1.5, 1.5, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(6);
            pdf.setFont('helvetica', 'bold');
            pdf.text(severityLabel, analysisX + 4, ay);

            pdf.setTextColor(150, 150, 150);
            pdf.setFontSize(6);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`${Math.round(ai.confidence * 100)}% confidence`, analysisX + badgeW + 5, ay);
            ay += 6;

            pdf.setTextColor(50, 50, 50);
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            const descLines = pdf.splitTextToSize(ai.description, analysisW - 4);
            pdf.text(descLines, analysisX + 2, ay);
            ay += descLines.length * 4.5 + 2;

            if (ai.detectedIssues.length > 0) {
              pdf.setFontSize(6.5);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(100, 100, 100);
              pdf.text('Detected Issues:', analysisX + 2, ay);
              ay += 4;
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(80, 80, 80);
              for (const issue of ai.detectedIssues) {
                const iLines = pdf.splitTextToSize(`• ${issue}`, analysisW - 6);
                pdf.text(iLines, analysisX + 2, ay);
                ay += iLines.length * 4;
              }
            }
          } else {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(analysisX, y, analysisW, imgH, 'F');
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(150, 150, 150);
            pdf.text('No AI analysis available', analysisX + analysisW / 2, y + imgH / 2, { align: 'center' });
          }

          y += analysisHeight + 6;
        } catch {
          // Skip photos that fail to load
        }
      }
    }
  }

  // ── Vehicle Photos ─────────────────────────────────────────

  if (inspection.vehiclePhotos && inspection.vehiclePhotos.length > 0) {
    const capturedPhotos = inspection.vehiclePhotos.filter(vp => vp.photo !== null);

    if (capturedPhotos.length > 0) {
      drawSectionHeader('Vehicle Photos');

      for (const vp of capturedPhotos) {
        const photo = vp.photo!;
        try {
          const imgData = await loadImageAsBase64(photo.uri);
          const imgProps = pdf.getImageProperties(imgData);
          const imgW = contentWidth / 2 - 5;
          const imgH = (imgProps.height / imgProps.width) * imgW;
          const analysisX = margin + imgW + 8;
          const analysisW = contentWidth / 2 - 5;

          const analysisLines = photo.aiAnalysis
            ? pdf.splitTextToSize(photo.aiAnalysis.description, analysisW - 4)
            : [];
          const issueCount = photo.aiAnalysis?.detectedIssues.length ?? 0;
          const blockH = Math.max(imgH, 12 + analysisLines.length * 4.5 + issueCount * 4 + 10);

          checkPageBreak(blockH + 12);

          const promptLabel = vp.promptId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          pdf.setFontSize(7.5);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(13, 27, 62);
          pdf.text(promptLabel, margin, y);
          y += 5;

          pdf.addImage(imgData, 'JPEG', margin, y, imgW, imgH);

          if (photo.aiAnalysis) {
            const ai = photo.aiAnalysis;
            pdf.setFillColor(248, 250, 252);
            pdf.rect(analysisX, y, analysisW, blockH, 'F');

            let ay = y + 5;

            const severityColors: Record<string, number[]> = {
              severe: [239, 68, 68],
              moderate: [245, 158, 11],
              minor: [234, 179, 8],
              none: [34, 197, 94],
            };
            const [sr, sg, sb] = severityColors[ai.severity] ?? [148, 163, 184];
            const severityLabel = ai.hasDamage ? ai.severity.toUpperCase() : 'NO DAMAGE';
            pdf.setFillColor(sr, sg, sb);
            const badgeW = pdf.getTextWidth(severityLabel) + 4;
            pdf.roundedRect(analysisX + 2, ay - 3, badgeW, 5, 1.5, 1.5, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(6);
            pdf.setFont('helvetica', 'bold');
            pdf.text(severityLabel, analysisX + 4, ay);

            pdf.setTextColor(150, 150, 150);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`${Math.round(ai.confidence * 100)}% confidence`, analysisX + badgeW + 5, ay);
            ay += 6;

            if (photo.isInvalid && photo.vehicleValidation) {
              pdf.setTextColor(239, 68, 68);
              pdf.setFontSize(6.5);
              pdf.setFont('helvetica', 'bold');
              pdf.text('⚠ Different vehicle detected', analysisX + 2, ay);
              ay += 4;
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(100, 100, 100);
              const reasonLines = pdf.splitTextToSize(photo.vehicleValidation.reasoning, analysisW - 4);
              pdf.text(reasonLines, analysisX + 2, ay);
              ay += reasonLines.length * 4 + 2;
            }

            pdf.setTextColor(50, 50, 50);
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            pdf.text(analysisLines, analysisX + 2, ay);
            ay += analysisLines.length * 4.5 + 2;

            if (ai.detectedIssues.length > 0) {
              pdf.setFontSize(6.5);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(100, 100, 100);
              pdf.text('Detected Issues:', analysisX + 2, ay);
              ay += 4;
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(80, 80, 80);
              for (const issue of ai.detectedIssues) {
                const iLines = pdf.splitTextToSize(`• ${issue}`, analysisW - 6);
                pdf.text(iLines, analysisX + 2, ay);
                ay += iLines.length * 4;
              }
            }
          } else {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(analysisX, y, analysisW, imgH, 'F');
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(150, 150, 150);
            pdf.text('No AI analysis available', analysisX + analysisW / 2, y + imgH / 2, { align: 'center' });
          }

          y += blockH + 6;
        } catch {
          // Skip photos that fail to load
        }
      }
    }
  }

  // ── Overall Notes ─────────────────────────────────────────

  if (inspection.overallNotes) {
    checkPageBreak(20);
    drawSectionHeader('Overall Notes');
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(50, 50, 50);
    const lines = pdf.splitTextToSize(inspection.overallNotes, contentWidth - 4);
    pdf.text(lines, margin + 2, y);
    y += lines.length * 5 + 4;
  }

  // ── Footer on every page ──────────────────────────────────

  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFillColor(245, 245, 245);
    pdf.rect(0, pageHeight - 10, pageWidth, 10, 'F');
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Wire3 Vehicle Inspection — Generated ${new Date().toLocaleString()}`, margin, pageHeight - 4);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }

  const fileName = `inspection-${inspection.vehicleUnitNumber}-${
    new Date(inspection.submittedAt ?? Date.now()).toISOString().split('T')[0]
  }.pdf`;
  pdf.save(fileName);
}

async function loadImageAsBase64(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}